/**
 * Pagination Component
 *
 * Generic pagination controls for list views with item selection support.
 * Displays page navigation controls and optional bulk delete actions.
 *
 * @module domains/personnel/presentation/components/shared/Pagination
 */

import { useTranslation } from 'react-i18next';
import { cn } from '@parrot-software/utils';
import { Button } from '@parrot-software/design-system';
import { ChevronLeft, ChevronRight } from '@parrot-software/design-system/icons';
import { getPageWindow } from '@personnel/presentation/utils';

/**
 * Props for Pagination component
 */
export interface PaginationProps {
  /** Current active page (1-indexed) */
  currentPage: number;
  /** Total number of pages available */
  totalPages: number;
  /** Total number of items across all pages */
  totalItems: number;
  /** Number of items currently selected */
  selectedCount?: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Optional callback when delete button is clicked (for bulk actions) */
  onDeleteClick?: () => void;
  /** Whether to show the row count info (default: true) */
  showRowCount?: boolean;
}

/**
 * Pagination Component
 *
 * Provides page navigation controls with optional bulk selection features.
 * Uses Semantic Styles Pattern for maintainability.
 *
 * @param {PaginationProps} props - Component properties
 * @returns {React.ReactElement} Pagination component
 *
 * @example
 * <Pagination
 *   currentPage={1}
 *   totalPages={10}
 *   totalItems={95}
 *   selectedCount={3}
 *   onPageChange={(page) => setCurrentPage(page)}
 *   onDeleteClick={() => handleBulkDelete()}
 * />
 */
/** Whether selection features should be shown */
const hasSelectionFeature = (onDeleteClick?: () => void) => !!onDeleteClick;

const styles = {
  container: 'flex flex-wrap items-center gap-3 w-full',
  containerWithSelection: 'justify-between',
  containerWithoutSelection: 'justify-end',
  selectionInfo: {
    wrapper: 'flex items-center gap-2',
    text: 'text-sm text-muted-foreground',
  },
  controls: {
    wrapper: 'flex items-center gap-1',
    button: 'h-9 px-3',
    buttonDisabled: 'opacity-50',
    buttonText: 'text-sm font-medium',
    pageButton: 'size-9',
    pageButtonActive: 'border border-border bg-background',
    ellipsis: 'size-9 flex items-center justify-center',
    ellipsisText: 'text-sm',
  },
  icon: 'size-4',
};

export const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  selectedCount = 0,
  onPageChange,
  onDeleteClick,
  showRowCount = true,
}: PaginationProps): React.ReactElement => {
  const { t } = useTranslation('personnel');

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleDeleteClick = () => {
    onDeleteClick?.();
  };

  const showSelectionInfo = showRowCount && hasSelectionFeature(onDeleteClick);
  const showRowCountOnly = showRowCount && !hasSelectionFeature(onDeleteClick);

  return (
    <div
      className={cn(
        styles.container,
        showSelectionInfo || showRowCountOnly ? styles.containerWithSelection : styles.containerWithoutSelection,
      )}
    >
      {/* Row Count - only shown when showRowCount is true */}
      {showRowCountOnly && (
        <div className={styles.selectionInfo.wrapper}>
          <p className={styles.selectionInfo.text}>{t('pagination.rowsTotal', { count: totalItems })}</p>
        </div>
      )}

      {/* Selection Info - only shown when selection feature is enabled */}
      {showSelectionInfo && (
        <div className={styles.selectionInfo.wrapper}>
          <p className={styles.selectionInfo.text}>
            {t('pagination.rowsSelected', { selected: selectedCount, total: totalItems })}
          </p>
          {selectedCount > 0 && (
            <Button onClick={handleDeleteClick} variant="destructive">
              {t('pagination.delete')}
            </Button>
          )}
        </div>
      )}

      {/* Pagination Controls */}
      <div className={styles.controls.wrapper}>
        {/* Previous Button */}
        <Button
          className={cn(styles.controls.button, currentPage === 1 && styles.controls.buttonDisabled)}
          disabled={currentPage === 1}
          onClick={handlePrevious}
          type="button"
          variant="ghost"
        >
          <ChevronLeft className={styles.icon} />
          <span className={styles.controls.buttonText}>{t('pagination.previous')}</span>
        </Button>

        {/* Page Numbers with sliding window and bidirectional ellipsis */}
        {getPageWindow(currentPage, totalPages).map((item) => {
          if (item === 'ellipsis-start' || item === 'ellipsis-end') {
            return (
              <div className={styles.controls.ellipsis} key={item}>
                <span className={styles.controls.ellipsisText}>...</span>
              </div>
            );
          }
          return (
            <Button
              className={cn(styles.controls.pageButton, item === currentPage && styles.controls.pageButtonActive)}
              key={item}
              onClick={() => onPageChange(item)}
              type="button"
              variant="ghost"
            >
              <span className={styles.controls.buttonText}>{item}</span>
            </Button>
          );
        })}

        {/* Next Button */}
        <Button
          className={cn(styles.controls.button, currentPage === totalPages && styles.controls.buttonDisabled)}
          disabled={currentPage === totalPages}
          onClick={handleNext}
          type="button"
          variant="ghost"
        >
          <span className={styles.controls.buttonText}>{t('pagination.next')}</span>
          <ChevronRight className={styles.icon} />
        </Button>
      </div>
    </div>
  );
};
